import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { whatsappWebApi } from '../api/whatsappWeb.api.js';
import { useAuthStore } from '../store/authStore.js';
import { IconRefresh, IconSend, IconWhatsApp, IconX } from '../components/icons/Icons.jsx';
import { contactPhoneLabel, contactPrimaryLabel } from '../utils/contactDisplay.js';

function statusTone(status) {
  if (status === 'CONNECTED') return { label: 'Connected', color: '#20C997', bg: 'rgba(32,201,151,.12)' };
  if (status === 'QR_PENDING') return { label: 'Scan QR', color: '#F59E0B', bg: 'rgba(245,158,11,.12)' };
  if (status === 'CONNECTING') return { label: 'Connecting', color: '#60A5FA', bg: 'rgba(96,165,250,.12)' };
  if (status === 'ERROR') return { label: 'Needs attention', color: '#EF4444', bg: 'rgba(239,68,68,.12)' };
  return { label: 'Disconnected', color: 'var(--textMuted)', bg: 'var(--card)' };
}

function ChatRow({ contact, onRead, onPresence }) {
  const last = contact.messages?.[0];
  const title = contactPrimaryLabel(contact);
  const meta = contactPhoneLabel(contact);
  const initial = title[0].toUpperCase();
  return (
    <div className="flex items-center gap-3 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
      <div className="w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ background: '#16A34A' }}>{initial}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <p className="font-bold truncate" style={{ color: 'var(--text)' }}>{title}</p>
          <p className="text-[11px] flex-shrink-0" style={{ color: 'var(--textDim)' }}>{last ? new Date(last.createdAt).toLocaleDateString() : ''}</p>
        </div>
        <p className="text-sm truncate" style={{ color: 'var(--textMuted)' }}>{last?.content || meta || 'No WhatsApp messages yet'}</p>
      </div>
      {contact._count?.messages > 0 && (
        <span className="min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
          {contact._count.messages}
        </span>
      )}
      <div className="hidden sm:flex items-center gap-1">
        <button className="btn-secondary !px-2 !py-1 text-[11px]" onClick={() => onPresence(contact.id, 'composing')}>Typing</button>
        <button className="btn-secondary !px-2 !py-1 text-[11px]" onClick={() => onRead(contact.id)}>Read</button>
      </div>
    </div>
  );
}

export default function WhatsAppWeb({ embedded = false }) {
  const tenant = useAuthStore((s) => s.tenant);
  const [status, setStatus] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pairPhone, setPairPhone] = useState('');
  const [pairCode, setPairCode] = useState('');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');
  const [sendMode, setSendMode] = useState('text');
  const [mediaUrl, setMediaUrl] = useState('');
  const [filename, setFilename] = useState('');
  const [checkPhone, setCheckPhone] = useState('');
  const [checkResult, setCheckResult] = useState(null);
  const tone = statusTone(status?.status || status?.persisted?.status);
  const connected = status?.ready || status?.status === 'CONNECTED';

  const accountText = useMemo(() => {
    const account = status?.account || status?.persisted?.account;
    return account?.phone || account?.name || 'No WhatsApp account linked yet';
  }, [status]);

  const load = async () => {
    try {
      const [statusRes, chatsRes] = await Promise.all([
        whatsappWebApi.status(),
        whatsappWebApi.chats(),
      ]);
      setStatus(statusRes.data);
      setChats(chatsRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, []);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await whatsappWebApi.connect();
      setStatus(data);
      toast.success('WhatsApp connection started');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not start WhatsApp');
    } finally {
      setConnecting(false);
    }
  };

  const getPairCode = async () => {
    try {
      const { data } = await whatsappWebApi.pairingCode(pairPhone);
      setPairCode(data.code);
      toast.success('Pairing code created');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create pairing code');
    }
  };

  const send = async (e) => {
    e.preventDefault();
    try {
      if (sendMode === 'image') {
        await whatsappWebApi.sendImage({ to, image: mediaUrl, caption: body });
      } else if (sendMode === 'document') {
        await whatsappWebApi.sendDocument({ to, document: mediaUrl, filename: filename || 'file', caption: body });
      } else {
        await whatsappWebApi.send({ to, body });
      }
      setBody('');
      setMediaUrl('');
      setFilename('');
      toast.success('WhatsApp message sent');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send WhatsApp');
    }
  };

  const checkNumber = async () => {
    setCheckResult(null);
    try {
      const { data } = await whatsappWebApi.checkNumber(checkPhone);
      setCheckResult(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not check number');
    }
  };

  const markRead = async (contactId) => {
    try {
      const { data } = await whatsappWebApi.markRead(contactId);
      toast.success(`Marked ${data.marked || 0} message(s) read`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not mark read');
    }
  };

  const sendPresence = async (contactId, state) => {
    try {
      await whatsappWebApi.presence(contactId, state);
      toast.success('Typing presence sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not send presence');
    }
  };

  const disconnect = async () => {
    if (!confirm('Disconnect this WhatsApp account from this workspace?')) return;
    try {
      const { data } = await whatsappWebApi.disconnect();
      setStatus(data);
      toast.success('WhatsApp disconnected');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not disconnect');
    }
  };

  if (!tenant?.whatsappWebEnabled) {
    return <div className="h-full flex items-center justify-center p-6" style={{ color: 'var(--textMuted)' }}>Direct WhatsApp is not enabled for this workspace.</div>;
  }

  return (
    <div className={embedded ? '' : 'h-full overflow-y-auto page-scroll'}>
      <div className={embedded ? 'space-y-5' : 'max-w-7xl mx-auto px-4 py-6 md:px-8 md:py-8 pb-28 md:pb-8 space-y-6'}>
        <div className={`flex flex-col md:flex-row ${embedded ? 'md:items-center md:justify-end' : 'md:items-end justify-between'} gap-4`}>
          {!embedded && (
            <div>
              <p className="eyebrow inline-flex items-center gap-2">
                <IconWhatsApp size={15} /> Direct WhatsApp
              </p>
              <h1 className="page-title">WhatsApp Web connection</h1>
              <p className="page-subtitle">
                Connect one WhatsApp account to this workspace. Messages are saved in this customer&apos;s inbox and never cross into another workspace.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button className="btn-secondary gap-2" onClick={load} disabled={loading}><IconRefresh size={16} /> Refresh</button>
            {connected && <button className="btn-secondary gap-2" onClick={disconnect}><IconX size={16} /> Disconnect</button>}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1.1fr_.9fr] gap-5">
          <section className="card p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Connection</h2>
                <p className="text-sm" style={{ color: 'var(--textMuted)' }}>{accountText}</p>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ color: tone.color, background: tone.bg }}>{tone.label}</span>
            </div>

            {status?.qrDataUrl ? (
              <div className="mt-5 grid md:grid-cols-[240px_1fr] gap-5 items-center">
                <div className="rounded-2xl bg-white p-3 w-fit">
                  <img src={status.qrDataUrl} alt="WhatsApp QR code" className="w-56 h-56" />
                </div>
                <div className="space-y-3">
                  <p className="font-bold" style={{ color: 'var(--text)' }}>Scan from WhatsApp</p>
                  <ol className="text-sm space-y-2" style={{ color: 'var(--textMuted)' }}>
                    <li>1. Open WhatsApp on the phone.</li>
                    <li>2. Go to Linked devices.</li>
                    <li>3. Tap Link a device and scan this QR.</li>
                  </ol>
                  <button className="btn-secondary gap-2" onClick={load}><IconRefresh size={16} /> I scanned it</button>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border p-5" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                <p className="font-bold" style={{ color: 'var(--text)' }}>{connected ? 'WhatsApp is ready' : 'Start by connecting WhatsApp'}</p>
                <p className="text-sm mt-1" style={{ color: 'var(--textMuted)' }}>
                  Use QR scan for the easiest setup. Pairing code is available if the phone camera cannot scan.
                </p>
                <button className="btn-primary mt-4 gap-2" onClick={connect} disabled={connecting}>
                  <IconWhatsApp size={17} /> {connecting ? 'Starting...' : connected ? 'Reconnect / refresh session' : 'Connect WhatsApp'}
                </button>
              </div>
            )}

            <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
              <p className="text-sm font-bold mb-2" style={{ color: 'var(--text)' }}>Pair with phone number</p>
              <div className="grid md:grid-cols-[1fr_auto] gap-2">
                <input className="input" value={pairPhone} onChange={e => setPairPhone(e.target.value)} placeholder="+15551234567" />
                <button className="btn-secondary" onClick={getPairCode}>Get code</button>
              </div>
              {pairCode && <div className="mt-3 text-2xl font-bold tracking-[.3em]" style={{ color: '#16A34A' }}>{pairCode}</div>}
            </div>

            {status?.lastError || status?.persisted?.lastError ? (
              <div className="mt-4 rounded-xl p-3 text-sm" style={{ background: 'rgba(239,68,68,.12)', color: '#FCA5A5' }}>
                {status.lastError || status.persisted.lastError}
              </div>
            ) : null}
          </section>

          <section className="card p-5">
            <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>Send WhatsApp</h2>
            <p className="text-sm mb-4" style={{ color: 'var(--textMuted)' }}>Send text, image, or document from the connected account.</p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              {['text', 'image', 'document'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className="rounded-xl px-3 py-2 text-xs font-bold capitalize"
                  onClick={() => setSendMode(mode)}
                  style={{
                    color: sendMode === mode ? 'white' : 'var(--textMuted)',
                    background: sendMode === mode ? '#16A34A' : 'var(--surface)',
                    border: '1px solid var(--border)',
                  }}
                >
                  {mode}
                </button>
              ))}
            </div>
            <form onSubmit={send} className="space-y-3">
              <input className="input" value={to} onChange={e => setTo(e.target.value)} placeholder="Recipient phone, e.g. +15551234567" />
              {sendMode !== 'text' && (
                <input className="input" value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder={sendMode === 'image' ? 'https://example.com/flyer.jpg' : 'https://example.com/file.pdf'} />
              )}
              {sendMode === 'document' && (
                <input className="input" value={filename} onChange={e => setFilename(e.target.value)} placeholder="Filename, e.g. ticket-info.pdf" />
              )}
              <textarea className="input min-h-32" value={body} onChange={e => setBody(e.target.value)} placeholder={sendMode === 'text' ? 'Type WhatsApp message...' : 'Caption (optional)'} />
              <div className="flex justify-between items-center gap-3">
                <span className="text-xs" style={{ color: 'var(--textDim)' }}>{body.length}/4000</span>
                <button className="btn-primary gap-2" disabled={!connected}><IconSend size={16} /> Send WhatsApp</button>
              </div>
            </form>
            <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
              <p className="text-sm font-bold mb-2" style={{ color: 'var(--text)' }}>Check number</p>
              <div className="grid md:grid-cols-[1fr_auto] gap-2">
                <input className="input" value={checkPhone} onChange={e => setCheckPhone(e.target.value)} placeholder="+15551234567" />
                <button className="btn-secondary" type="button" disabled={!connected} onClick={checkNumber}>Check</button>
              </div>
              {checkResult && (
                <p className="text-sm mt-3 font-bold" style={{ color: checkResult.registered ? '#20C997' : '#FCA5A5' }}>
                  {checkResult.phone} {checkResult.registered ? 'is on WhatsApp' : 'was not found on WhatsApp'}
                </p>
              )}
            </div>
          </section>
        </div>

        <section className="card p-5">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>WhatsApp inbox sync</h2>
              <p className="text-sm" style={{ color: 'var(--textMuted)' }}>Recent contacts with WhatsApp messages. Full conversations also appear in the main Inbox.</p>
            </div>
            <span className="text-xs font-bold" style={{ color: 'var(--textDim)' }}>{chats.length} chats</span>
          </div>
          {chats.length ? chats.map((contact) => (
            <ChatRow key={contact.id} contact={contact} onRead={markRead} onPresence={sendPresence} />
          )) : (
            <div className="py-16 text-center" style={{ color: 'var(--textMuted)' }}>
              <IconWhatsApp size={34} className="mx-auto mb-3" />
              No WhatsApp chats yet.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
